import { useState } from 'react';
import { Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { assetsApi, type ServiceAsset, type AssetInput } from '../../api/amc.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const empty = { siteId: '', chargerBrand: '', chargerModel: '', serialNumber: '', gunSerialNumber: '', capacityKw: '', connectorType: '', commissionDate: '', warrantyStart: '', warrantyEnd: '', amcStart: '', amcEnd: '' };

export function AssetsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(empty);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['service-assets', page, limit, search], queryFn: () => assetsApi.list({ page, limit, search: search || undefined }) });
  const save = useMutation({
    mutationFn: () => {
      const num = (v: string) => (v === '' ? undefined : Number(v));
      const input: AssetInput & { siteId: number } = {
        siteId: Number(form.siteId), chargerBrand: form.chargerBrand || undefined, chargerModel: form.chargerModel || undefined,
        serialNumber: form.serialNumber || undefined, gunSerialNumber: form.gunSerialNumber || undefined, capacityKw: num(form.capacityKw),
        connectorType: form.connectorType || undefined, commissionDate: form.commissionDate || undefined,
        warrantyStart: form.warrantyStart || undefined, warrantyEnd: form.warrantyEnd || undefined, amcStart: form.amcStart || undefined, amcEnd: form.amcEnd || undefined,
      };
      return assetsApi.commission(input);
    },
    onSuccess: () => { setOpen(false); qc.invalidateQueries({ queryKey: ['service-assets'] }); },
    onError: (e) => setError(extractApiError(e)),
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const columns: Column<ServiceAsset>[] = [
    { key: 'code', label: 'Asset Code', width: 150 },
    { key: 'charger', label: 'Charger', render: (r) => `${r.chargerBrand ?? '—'} ${r.chargerModel ?? ''}` },
    { key: 'serialNumber', label: 'Serial', render: (r) => r.serialNumber ?? '—' },
    { key: 'capacityKw', label: 'kW', align: 'right', render: (r) => r.capacityKw ?? '—' },
    { key: 'amcEnd', label: 'AMC End', render: (r) => r.amcEnd ?? '—' },
    { key: 'healthStatus', label: 'Health', render: (r) => <StatusBadge status={r.healthStatus} /> },
    { key: 'currentStatus', label: 'Status', render: (r) => <Chip size="small" label={r.currentStatus.replace(/_/g, ' ')} variant="outlined" /> },
  ];

  return (
    <>
      <PageHeader title="Installed Asset Registry" subtitle="Every commissioned charger as a service asset — warranty, AMC and health." action={hasPermission('service_asset.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setForm(empty); setError(null); setOpen(true); }}>Commission Asset</Button>} />

      <TableToolbar search={search} onSearch={(v) => { setSearch(v); setPage(1); }} searchPlaceholder="Search code, serial, brand…" />
      <DataTable columns={columns} rows={list.data?.data ?? []} loading={list.isLoading} page={page} limit={limit} total={list.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id} emptyTitle="No assets commissioned" />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Commission Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Site ID" type="number" value={form.siteId} onChange={(e) => set('siteId', e.target.value)} helperText="Client / project / region are derived from the site" />
            <Stack direction="row" spacing={2}>
              <TextField label="Charger Brand" value={form.chargerBrand} onChange={(e) => set('chargerBrand', e.target.value)} fullWidth />
              <TextField label="Model" value={form.chargerModel} onChange={(e) => set('chargerModel', e.target.value)} fullWidth />
              <TextField label="Capacity (kW)" type="number" value={form.capacityKw} onChange={(e) => set('capacityKw', e.target.value)} sx={{ width: 140 }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Serial Number" value={form.serialNumber} onChange={(e) => set('serialNumber', e.target.value)} fullWidth />
              <TextField label="Gun Serial" value={form.gunSerialNumber} onChange={(e) => set('gunSerialNumber', e.target.value)} fullWidth />
              <TextField label="Connector" value={form.connectorType} onChange={(e) => set('connectorType', e.target.value)} sx={{ width: 140 }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Commission Date" type="date" InputLabelProps={{ shrink: true }} value={form.commissionDate} onChange={(e) => set('commissionDate', e.target.value)} fullWidth />
              <TextField label="Warranty Start" type="date" InputLabelProps={{ shrink: true }} value={form.warrantyStart} onChange={(e) => set('warrantyStart', e.target.value)} fullWidth />
              <TextField label="Warranty End" type="date" InputLabelProps={{ shrink: true }} value={form.warrantyEnd} onChange={(e) => set('warrantyEnd', e.target.value)} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="AMC Start" type="date" InputLabelProps={{ shrink: true }} value={form.amcStart} onChange={(e) => set('amcStart', e.target.value)} fullWidth />
              <TextField label="AMC End" type="date" InputLabelProps={{ shrink: true }} value={form.amcEnd} onChange={(e) => set('amcEnd', e.target.value)} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.siteId || save.isPending} onClick={() => save.mutate()}>Commission</Button></DialogActions>
      </Dialog>
    </>
  );
}
